import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot, hydrateRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import { SSRDataProvider, getClientSSRData } from "./contexts/SSRDataContext";
import "./index.css";

// ResizeObserver loop completed is a benign browser notification, not a real error.
// It fires when layout changes happen faster than the observer can process them.
if (typeof window !== "undefined") {
  const _origOnError = window.onerror;
  window.onerror = (msg, ...args) => {
    if (typeof msg === "string" && msg.includes("ResizeObserver loop")) return true;
    return _origOnError ? (_origOnError as typeof window.onerror)!(msg, ...args) : false;
  };
}

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

const rootElement = document.getElementById("root")!;
const ssrData = getClientSSRData();

const appJsx = (
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <SSRDataProvider data={ssrData}>
        <App />
      </SSRDataProvider>
    </QueryClientProvider>
  </trpc.Provider>
);

// Wenn SSR-Content vorhanden ist, hydrieren statt neu rendern
if (rootElement.dataset.ssr === "true") {
  hydrateRoot(rootElement, appJsx);
} else {
  createRoot(rootElement).render(appJsx);
}
